import type { PathPattern } from "react-router";
import { matchesRouterPath } from "@/app/router/router-path";
import {
  ADMIN_FINANCE_ROLE_CLAIMS,
  ADMIN_OPERATOR_ROLE_CLAIMS,
  CMS_EDITOR_ROLE_CLAIMS,
  PEOPLE_OPERATOR_ROLE_CLAIMS,
} from "@/core/auth/admin-role-tiers";
import type { ModuleManifest, ModuleReadinessGate } from "@/types/clm-web";

const foundationalGate: ModuleReadinessGate = {
  moduleId: "dashboard",
  jiraStory: "CLMS-96",
  backendEpic: "Epic 11 frontend scaffold",
  productConfirmation: "confirmed",
  apiEvidence: "none",
  implementationStatus: "plan-ready",
  notes: "Shell-owned scaffold module; no live backend module API dependency.",
};

const notReadyGate = (gate: ModuleReadinessGate): ModuleReadinessGate => ({
  ...gate,
  productConfirmation: gate.productConfirmation,
  apiEvidence: "none",
  implementationStatus: "blocked",
});

export const MODULE_MANIFESTS = [
  {
    moduleId: "dashboard",
    routeBase: "/dashboard",
    displayName: "Dashboard",
    requiredRoles: ["authenticated"],
    navIcon: "dashboard",
    lazyEntryPoint: "features/dashboard/dashboard-page",
    apiContractStatus: "not-ready",
    readinessGate: foundationalGate,
    futureHostAlias: null,
    protected: true,
    navigationGroup: "core",
  },
  {
    moduleId: "admin",
    routeBase: "/admin",
    displayName: "Admin",
    // Every carried operator tier is admitted server-side (CLMS-116 T057).
    // Literal on both sides: the client route guards and this module gate
    // match the operator claims literally, mirroring the server — the
    // owner/super_admin escalation claims never admit alone.
    requiredRoles: [...ADMIN_OPERATOR_ROLE_CLAIMS],
    roleMatch: "literal",
    navIcon: "admin",
    lazyEntryPoint: "features/admin/admin-routes",
    apiContractStatus: "sandbox-api-ready",
    readinessGate: {
      moduleId: "admin",
      jiraStory: "CLMS-116",
      backendEpic: "Admin people and adjudication workflows (clm-web-service-auth /admin)",
      productConfirmation: "confirmed",
      apiEvidence: "sandbox-api",
      implementationStatus: "implementation-ready",
      notes:
        "People administration (list/create/edit), pending-application adjudication, and the admin identities surface (CLMS-124 US1\u2013US3: review, grant, deactivate/reactivate) are live; /admin renders the Admin dashboard landing reflecting delivered surfaces, and remaining admin subpaths stay placeholder-gated.",
    },
    futureHostAlias: null,
    protected: true,
    navigationGroup: "admin",
    deliveredSurfaces: [
      {
        id: "people",
        title: "People",
        description:
          "Find and maintain people and their user accounts, and adjudicate pending applications.",
        route: "/admin/people",
        // Server admits the People operator tiers (Admin, Admin Support Staff,
        // Membership Coordinator) to People and adjudication; Courses Admin
        // admits to the realm but holds no People permissions (D17). Literal
        // matching mirrors the server rule that escalation modifiers never
        // admit on their own.
        requiredRoles: PEOPLE_OPERATOR_ROLE_CLAIMS,
        roleMatch: "literal",
      },
      {
        id: "identities",
        title: "Identities",
        description: "Review, grant, and manage admin identities and their lifecycle status.",
        route: "/admin/identities",
        icon: "member",
        // Identities read/list is gated on the LITERAL Admin claim server-side
        // (CLMS-116 T059); literal matching stops hasRole's owner/super_admin
        // expansion from surfacing a tile the server would 403.
        requiredRoles: ["admin"],
        roleMatch: "literal",
      },
    ],
  },
  {
    moduleId: "member",
    routeBase: "/member",
    displayName: "Member",
    requiredRoles: ["member"],
    navIcon: "member",
    lazyEntryPoint: "features/member/member-routes",
    apiContractStatus: "not-ready",
    readinessGate: notReadyGate({
      moduleId: "member",
      jiraStory: "CLMS-80",
      backendEpic: "Auth shell and member area boundary",
      productConfirmation: "pending",
      apiEvidence: "none",
      implementationStatus: "blocked",
      notes: "Member routes consume effective-user context when admin acting-as is active.",
    }),
    futureHostAlias: null,
    protected: true,
    navigationGroup: "member",
  },
  {
    moduleId: "financial",
    routeBase: "/financial",
    displayName: "Financial",
    requiredRoles: ["financial:read"],
    navIcon: "financial",
    lazyEntryPoint: "features/financial/financial-routes",
    apiContractStatus: "not-ready",
    readinessGate: notReadyGate({
      moduleId: "financial",
      jiraStory: "CLMS-50",
      backendEpic: "EPIC 6",
      productConfirmation: "pending",
      apiEvidence: "none",
      implementationStatus: "blocked",
      notes:
        "Requires Stripe test-mode/payment/refund contract evidence before API-backed implementation.",
    }),
    futureHostAlias: null,
    protected: true,
    navigationGroup: "modules",
  },
  {
    moduleId: "industry-resource-directory",
    routeBase: "/industry-resource-directory",
    displayName: "Industry Resource Directory",
    requiredRoles: ["directory:read"],
    navIcon: "directory",
    lazyEntryPoint: "features/industry-resource-directory/directory-routes",
    apiContractStatus: "not-ready",
    readinessGate: notReadyGate({
      moduleId: "industry-resource-directory",
      jiraStory: "CLMS-72",
      backendEpic: "Epic 11 frontend scaffold",
      productConfirmation: "pending",
      apiEvidence: "none",
      implementationStatus: "blocked",
      notes: "Legacy area boundary only; concrete API behavior is not invented in Phase 2.",
    }),
    futureHostAlias: null,
    protected: true,
    navigationGroup: "modules",
  },
  {
    moduleId: "jobs",
    routeBase: "/jobs",
    displayName: "Jobs",
    requiredRoles: ["jobs:read"],
    navIcon: "jobs",
    lazyEntryPoint: "features/jobs/jobs-routes",
    apiContractStatus: "not-ready",
    readinessGate: notReadyGate({
      moduleId: "jobs",
      jiraStory: "CLMS-72",
      backendEpic: "Epic 11 frontend scaffold",
      productConfirmation: "pending",
      apiEvidence: "none",
      implementationStatus: "blocked",
      notes: "Legacy area boundary only; future implementation requires product/backend evidence.",
    }),
    futureHostAlias: null,
    protected: true,
    navigationGroup: "modules",
  },
  {
    moduleId: "ucc",
    routeBase: "/ucc",
    displayName: "UCC",
    requiredRoles: ["ucc:read"],
    navIcon: "ucc",
    lazyEntryPoint: "features/ucc/ucc-routes",
    apiContractStatus: "not-ready",
    readinessGate: notReadyGate({
      moduleId: "ucc",
      jiraStory: "CLMS-63",
      backendEpic: "EPIC 2",
      productConfirmation: "blocked",
      apiEvidence: "none",
      implementationStatus: "blocked",
      notes:
        "Requirements are changing/unclear; UCC is blocked pending clarification and must not be selected as first module.",
    }),
    futureHostAlias: null,
    protected: true,
    navigationGroup: "modules",
  },
  {
    moduleId: "events",
    routeBase: "/events",
    displayName: "Events",
    requiredRoles: ["events:read"],
    navIcon: "events",
    lazyEntryPoint: "features/events/events-routes",
    apiContractStatus: "not-ready",
    readinessGate: notReadyGate({
      moduleId: "events",
      jiraStory: "CLMS-52",
      backendEpic: "EPIC 3",
      productConfirmation: "pending",
      apiEvidence: "none",
      implementationStatus: "blocked",
      notes:
        "First domain UI candidate after product confirmation and API/contract/fixture evidence.",
    }),
    futureHostAlias: null,
    protected: true,
    navigationGroup: "modules",
  },
  {
    moduleId: "courses",
    routeBase: "/courses",
    displayName: "Courses",
    // 004-6 T048: members reach the learner catalog and My Courses; the
    // staff permission admits the admin-realm operator tiers (D17).
    requiredRoles: ["courses:view", "member"],
    navIcon: "courses",
    lazyEntryPoint: "features/courses/courses-routes",
    apiContractStatus: "sandbox-api-ready",
    readinessGate: {
      moduleId: "courses",
      jiraStory: "CLMS-158",
      backendEpic: "EPIC 4 (clm-web-service-courses, Moodle LMS integration 004-6)",
      productConfirmation: "confirmed",
      apiEvidence: "sandbox-api",
      implementationStatus: "implementation-ready",
      notes:
        "Available module (owner confirmation 2026-09-27): clm-web-service-courses /health is deployed in every stage and Moodle administration is live; /courses lists the delivered surfaces (Moodle administration for courses:manage on the admin realm; the learner catalog, course detail with Buy, and My Courses with Launch for members on the standard realm — 004-6 T048/T049).",
    },
    futureHostAlias: null,
    protected: true,
    navigationGroup: "modules",
    // D17: Courses Admin is an admin-realm operator tier, so AdminRealmRouter
    // serves courses/* too and the Admin dashboard lists the module.
    realms: ["standard", "admin"],
  },
  {
    moduleId: "compliance",
    routeBase: "/compliance",
    displayName: "Compliance",
    requiredRoles: ["compliance:read"],
    navIcon: "compliance",
    lazyEntryPoint: "features/compliance/compliance-routes",
    apiContractStatus: "not-ready",
    readinessGate: notReadyGate({
      moduleId: "compliance",
      jiraStory: "CLMS-59",
      backendEpic: "EPIC 5",
      productConfirmation: "pending",
      apiEvidence: "none",
      implementationStatus: "blocked",
      notes:
        "Requires compliance state-machine and CE validation contract evidence before implementation.",
    }),
    futureHostAlias: null,
    protected: true,
    navigationGroup: "modules",
  },
  {
    moduleId: "documents",
    routeBase: "/documents",
    displayName: "Documents",
    requiredRoles: ["documents:read"],
    navIcon: "documents",
    lazyEntryPoint: "features/documents/documents-routes",
    apiContractStatus: "not-ready",
    readinessGate: notReadyGate({
      moduleId: "documents",
      jiraStory: "CLMS-65",
      backendEpic: "EPIC 7",
      productConfirmation: "pending",
      apiEvidence: "none",
      implementationStatus: "blocked",
      notes:
        "Requires upload/download, PDF preview, and DocuSign polling contract evidence before implementation.",
    }),
    futureHostAlias: null,
    protected: true,
    navigationGroup: "modules",
  },
  {
    moduleId: "search",
    routeBase: "/search",
    displayName: "Search",
    requiredRoles: ["search:read"],
    navIcon: "search",
    lazyEntryPoint: "features/search/search-routes",
    apiContractStatus: "not-ready",
    readinessGate: notReadyGate({
      moduleId: "search",
      jiraStory: "CLMS-69",
      backendEpic: "EPIC 8",
      productConfirmation: "pending",
      apiEvidence: "none",
      implementationStatus: "blocked",
      notes:
        "Requires OpenSearch/search API readiness and 500 ms target evidence before implementation.",
    }),
    futureHostAlias: null,
    protected: true,
    navigationGroup: "utility",
  },
] as const satisfies readonly ModuleManifest[];

/** Ids with a MODULE_MANIFESTS entry: the only ids the manifest and readiness lookups resolve. */
export type ManifestModuleId = (typeof MODULE_MANIFESTS)[number]["moduleId"];

/**
 * Admin-realm financial module (006-9 UI contract §8a.1/§12, CLMS-190): a
 * minimal ready manifest carrying the staff invoice routes. Kept outside
 * MODULE_MANIFESTS so the standard-realm financial placeholder stays blocked.
 */
export const ADMIN_FINANCIAL_MODULE_MANIFEST: ModuleManifest = {
  moduleId: "financial",
  routeBase: "/admin/financial",
  displayName: "Financial",
  requiredRoles: [...ADMIN_FINANCE_ROLE_CLAIMS],
  roleMatch: "literal",
  navIcon: "financial",
  lazyEntryPoint: "features/financial/staff-invoices/routes/staff-invoice-list",
  apiContractStatus: "contract-checked-in",
  readinessGate: {
    moduleId: "financial",
    jiraStory: "CLMS-190",
    backendEpic: "017 financial contract domain (clm-web-service-financial /staff/invoices)",
    productConfirmation: "confirmed",
    apiEvidence: "openapi",
    implementationStatus: "implementation-ready",
    notes:
      "Barebones staff invoice list/detail with the invoice notice resend action; CLMS-50 extends it.",
  },
  futureHostAlias: null,
  protected: true,
  navigationGroup: "modules",
  realms: ["admin"],
  deliveredSurfaces: [
    {
      id: "invoices",
      title: "Invoices",
      description: "Review invoices and resend invoice emails.",
      route: "/admin/financial/invoices",
      icon: "financial",
      requiredRoles: ADMIN_FINANCE_ROLE_CLAIMS,
      roleMatch: "literal",
    },
  ],
};

/**
 * Admin-realm CMS entry (011-CLMS-237 contract clm-web-runtime-config.md § Admin
 * entry): one "Manage content" tile for the CMS editor roles while
 * `cmsModuleEnabled` is on. Kept outside MODULE_MANIFESTS like the financial
 * manifest, so it adds no sidebar entry or readiness registry row.
 */
export const ADMIN_CONTENT_MODULE_MANIFEST: ModuleManifest = {
  moduleId: "cms",
  routeBase: "/admin/content",
  displayName: "Content",
  requiredRoles: [...CMS_EDITOR_ROLE_CLAIMS],
  roleMatch: "literal",
  navIcon: "documents",
  lazyEntryPoint: "features/admin/content/content-handoff-page",
  apiContractStatus: "sandbox-api-ready",
  readinessGate: {
    moduleId: "cms",
    jiraStory: "CLMS-237",
    backendEpic: "011 CMS platform foundation (clm-web-service-cms session hand-off)",
    productConfirmation: "confirmed",
    apiEvidence: "sandbox-api",
    implementationStatus: "implementation-ready",
    notes: "Hand-off to the CMS admin; listed only while cmsModuleEnabled is on.",
  },
  futureHostAlias: null,
  protected: true,
  navigationGroup: "modules",
  realms: ["admin"],
  featureFlag: "cmsModuleEnabled",
  deliveredSurfaces: [
    {
      id: "handoff",
      title: "Manage content",
      description: "Edit and publish site copy in the CLM content management system.",
      route: "/admin/content/handoff",
      icon: "documents",
      requiredRoles: CMS_EDITOR_ROLE_CLAIMS,
      roleMatch: "literal",
    },
  ],
};

/**
 * Manifests served on the admin realm: the standard set plus the admin-realm
 * financial and content modules, so the admin landing can surface their
 * delivered tiles while MODULE_MANIFESTS stays unchanged for the standard realm.
 */
export const ADMIN_DASHBOARD_MANIFESTS: readonly ModuleManifest[] = [
  ...MODULE_MANIFESTS,
  ADMIN_FINANCIAL_MODULE_MANIFEST,
  ADMIN_CONTENT_MODULE_MANIFEST,
];

/** The admin financial module's route base and every path beneath it (never a sibling sharing the prefix). */
export const ADMIN_FINANCIAL_PATH_PATTERN: PathPattern = {
  path: `${ADMIN_FINANCIAL_MODULE_MANIFEST.routeBase}/*`,
};

export function isAdminFinancialPath(pathname: string): boolean {
  return matchesRouterPath(ADMIN_FINANCIAL_PATH_PATTERN.path, pathname);
}

export function getModuleManifest(moduleId: ManifestModuleId): ModuleManifest {
  const manifest = MODULE_MANIFESTS.find((candidate) => candidate.moduleId === moduleId);

  if (!manifest) {
    throw new Error(`Unknown CLM Web module: ${moduleId}`);
  }

  return manifest;
}

export function getProtectedModuleManifests(): readonly ModuleManifest[] {
  return MODULE_MANIFESTS.filter((manifest) => manifest.protected);
}
